import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Fragment } from '@atlaskit/editor-prosemirror/model';

/**
 * Compare valid requested replacement structure with the applied target. Parse through
 * the editor schema to fill default attrs; localIds and editor-only attrs are not intent.
 * Text is deliberately excluded: this signal measures structural loss, not copy changes.
 */
function stableValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(stableValue);
	}
	if (value !== null && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value)
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([key, item]) => [key, stableValue(item)]),
		);
	}
	return value;
}

function comparableAttrs(values: Record<string, unknown>): Record<string, unknown> {
	return Object.fromEntries(
		Object.entries(values).filter(([key]) => key !== 'localId' && !key.startsWith('__')),
	);
}

function structure(node: PMNode): unknown[] {
	const children: unknown[][] = [];
	node.forEach((child) => {
		// Adjacent text runs can legitimately merge during application.
		if (!child.isText) {
			children.push(structure(child));
		}
	});
	return [node.type.name, stableValue(comparableAttrs(node.attrs)), children];
}

/** A bounded node-type tree. Never send text or attribute values to analytics. */
function summarizeNodeTypes(nodes: (PMNode | undefined)[]): {
	summary: string;
	truncated: boolean;
} {
	const maxNodes = 32;
	const maxDepth = 8;
	let count = 0;
	let truncated = false;
	const visit = (node: PMNode, depth: number): string => {
		if (count >= maxNodes || depth >= maxDepth) {
			truncated = true;
			return '…';
		}
		count++;
		const children: string[] = [];
		let omittedChildren = false;
		node.forEach((child) => {
			if (child.isText || omittedChildren) {
				return;
			}
			if (count >= maxNodes || depth + 1 >= maxDepth) {
				truncated = true;
				children.push('…');
				omittedChildren = true;
				return;
			}
			children.push(visit(child, depth + 1));
		});
		return children.length ? `${node.type.name}(${children.join(',')})` : node.type.name;
	};
	const summaries: string[] = [];
	for (const node of nodes) {
		if (count >= maxNodes) {
			truncated = true;
			summaries.push('…');
			break;
		}
		if (node) {
			summaries.push(visit(node, 0));
		} else {
			count++;
			summaries.push('missing');
		}
	}
	return {
		summary: summaries.join(','),
		truncated,
	};
}

function differingAttributeNames(expected: PMNode[], actual: (PMNode | undefined)[]): string[] {
	const names = new Set<string>();
	let comparedNodes = 0;
	const compare = (left: PMNode, right: PMNode, depth: number): void => {
		if (left.type !== right.type || depth >= 8 || comparedNodes >= 32 || names.size >= 16) {
			return;
		}
		comparedNodes++;
		const leftAttrs = comparableAttrs(left.attrs);
		const rightAttrs = comparableAttrs(right.attrs);
		for (const key of new Set([...Object.keys(leftAttrs), ...Object.keys(rightAttrs)])) {
			if (
				JSON.stringify(stableValue(leftAttrs[key])) !== JSON.stringify(stableValue(rightAttrs[key]))
			) {
				names.add(key);
			}
			if (names.size >= 16) {
				return;
			}
		}
		const leftChildren: PMNode[] = [];
		const rightChildren: PMNode[] = [];
		left.forEach((child) => {
			if (!child.isText) {
				leftChildren.push(child);
			}
		});
		right.forEach((child) => {
			if (!child.isText) {
				rightChildren.push(child);
			}
		});
		leftChildren.forEach((child, index) => {
			if (rightChildren[index]) {
				compare(child, rightChildren[index], depth + 1);
			}
		});
	};
	expected.forEach((node, index) => {
		if (actual[index]) {
			compare(node, actual[index], 0);
		}
	});
	return [...names];
}

export type ReplacementStructureComparison =
	| { mismatch: false }
	| {
			actualStructure: string;
			comparisonScope: 'ancestor' | 'replacement';
			differingAttributeNames?: string[];
			expectedStructure: string;
			mismatch: true;
			structureTruncated: boolean;
	  };

function mismatchDetails(
	expected: PMNode[],
	actual: (PMNode | undefined)[],
	comparisonScope: 'ancestor' | 'replacement',
): ReplacementStructureComparison {
	const expectedSummary = summarizeNodeTypes(expected);
	const actualSummary = summarizeNodeTypes(actual);
	const attributeNames = differingAttributeNames(expected, actual);
	return {
		mismatch: true,
		expectedStructure: expectedSummary.summary,
		actualStructure: actualSummary.summary || 'missing',
		structureTruncated: expectedSummary.truncated || actualSummary.truncated,
		comparisonScope,
		...(attributeNames.length ? { differingAttributeNames: attributeNames } : {}),
	};
}

/**
 * `undefined` means the request cannot safely be verified (partial/invalid ADF,
 * unresolved target, or replacement requiring schema fitting). Never blame the apply
 * layer for those cases. The raw final chunk is used before streamer transforms can
 * flatten requested wrappers. Only JSON ADF replaceNode responses are verified here.
 */
export function compareReplacementStructure(
	before: PMNode,
	after: PMNode,
	targetLocalId: unknown,
	response: string,
): ReplacementStructureComparison | undefined {
	if (typeof targetLocalId !== 'string' || targetLocalId.length === 0) {
		return undefined;
	}
	try {
		const requested = JSON.parse(response);
		if (
			requested?.type !== 'doc' ||
			!Array.isArray(requested.content) ||
			!requested.content.length
		) {
			return undefined;
		}
		const replacement = Fragment.fromArray(
			requested.content.map((json: unknown) => before.type.schema.nodeFromJSON(json)),
		);
		replacement.forEach((node) => node.check());
		let targetPos: number | undefined;
		// Stop at the second match: duplicate IDs make the applied region ambiguous.
		const findTarget = (parent: PMNode, start: number): boolean => {
			let pos = start;
			for (let index = 0; index < parent.childCount; index++) {
				const node = parent.child(index);
				if (node.attrs.localId === targetLocalId) {
					if (targetPos !== undefined) {
						return false;
					}
					targetPos = pos;
				}
				if (!findTarget(node, pos + 1)) {
					return false;
				}
				pos += node.nodeSize;
			}
			return true;
		};
		if (!findTarget(before, 0) || targetPos === undefined) {
			return undefined;
		}
		const resolved = before.resolve(targetPos);
		// List replacements can legitimately unwrap or lift out of the target's
		// ancestors during slice fitting. The queue may already receive a synthetic
		// task/decision-list wrapper, so these are not safe structural comparisons.
		const listTypes = new Set(['bulletList', 'orderedList', 'taskList', 'decisionList']);
		let replacesWithList = false;
		replacement.forEach((node) => {
			replacesWithList ||= listTypes.has(node.type.name);
		});
		if (replacesWithList) {
			for (let depth = resolved.depth; depth > 0; depth--) {
				if (listTypes.has(resolved.node(depth).type.name)) {
					return undefined;
				}
			}
		}
		const index = resolved.index();
		if (!resolved.parent.canReplace(index, index + 1, replacement)) {
			return undefined;
		}
		// Follow the original target's ancestry, not its localId: replacements may
		// regenerate every ID. Comparing only this region avoids unrelated panels
		// elsewhere in the document masking a dropped wrapper.
		let parent = after;
		for (let depth = 0; depth < resolved.depth; depth++) {
			const child = parent.maybeChild(resolved.index(depth));
			if (!child || child.type !== resolved.node(depth + 1).type) {
				return mismatchDetails([resolved.node(depth + 1)], child ? [child] : [], 'ancestor');
			}
			parent = child;
		}
		let mismatch = false;
		const expectedNodes: PMNode[] = [];
		const actualNodes: (PMNode | undefined)[] = [];
		replacement.forEach((node, _offset, childIndex) => {
			const applied = parent.maybeChild(index + childIndex);
			expectedNodes.push(node);
			actualNodes.push(applied ?? undefined);
			if (!applied || JSON.stringify(structure(node)) !== JSON.stringify(structure(applied))) {
				mismatch = true;
			}
		});
		return mismatch
			? mismatchDetails(expectedNodes, actualNodes, 'replacement')
			: { mismatch: false };
	} catch {
		return undefined;
	}
}
